/**
 * Fiscal year utilities (July 1 – June 30).
 * Mirrors enterprise-backend/app/utils/fiscal_year.py
 */

const FISCAL_YEAR_START_MONTH = 7;
const FISCAL_YEAR_END_MONTH = 6;
const FISCAL_YEAR_END_DAY = 30;

function toDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) return parsed;
  const [y, m, d] = String(value).split(/[-/]/).map(Number);
  if (y && m && d) return new Date(y, m - 1, d);
  return null;
}

export function getCurrentFyEnd(referenceDate) {
  const d = toDate(referenceDate);
  if (!d) return null;
  const year = d.getMonth() + 1 >= FISCAL_YEAR_START_MONTH ? d.getFullYear() + 1 : d.getFullYear();
  return new Date(year, FISCAL_YEAR_END_MONTH - 1, FISCAL_YEAR_END_DAY);
}

export function getLastFyEnd(referenceDate) {
  const cfy = getCurrentFyEnd(referenceDate);
  if (!cfy) return null;
  return new Date(cfy.getFullYear() - 1, FISCAL_YEAR_END_MONTH - 1, FISCAL_YEAR_END_DAY);
}

export function getNextFyEnd(referenceDate) {
  const cfy = getCurrentFyEnd(referenceDate);
  if (!cfy) return null;
  return new Date(cfy.getFullYear() + 1, FISCAL_YEAR_END_MONTH - 1, FISCAL_YEAR_END_DAY);
}

const PERIOD_OFFSETS = {
  'LFY-1': -2,
  LFY: -1,
  CFY: 0,
  NFY: 1,
  'NFY+1': 2,
  'NFY+2': 3,
};

export function getFyEndForPeriod(referenceDate, period) {
  const cfyEnd = getCurrentFyEnd(referenceDate);
  if (!cfyEnd || !(period in PERIOD_OFFSETS)) return null;
  const offset = PERIOD_OFFSETS[period];
  return new Date(
    cfyEnd.getFullYear() + offset,
    FISCAL_YEAR_END_MONTH - 1,
    FISCAL_YEAR_END_DAY,
  );
}

export function getFiscalPeriodEnds(referenceDate) {
  const cfyEnd = getCurrentFyEnd(referenceDate);
  if (!cfyEnd) return {};
  const year = cfyEnd.getFullYear();
  return {
    'LFY-1': new Date(year - 2, FISCAL_YEAR_END_MONTH - 1, FISCAL_YEAR_END_DAY),
    LFY: new Date(year - 1, FISCAL_YEAR_END_MONTH - 1, FISCAL_YEAR_END_DAY),
    CFY: cfyEnd,
    NFY: new Date(year + 1, FISCAL_YEAR_END_MONTH - 1, FISCAL_YEAR_END_DAY),
    'NFY+1': new Date(year + 2, FISCAL_YEAR_END_MONTH - 1, FISCAL_YEAR_END_DAY),
    'NFY+2': new Date(year + 3, FISCAL_YEAR_END_MONTH - 1, FISCAL_YEAR_END_DAY),
  };
}

function pad(n) {
  return String(n).padStart(2, '0');
}

export function formatDateMdy(date) {
  if (!date) return '';
  const d = toDate(date);
  if (!d) return '';
  return `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${d.getFullYear()}`;
}

export function formatFyEnd(referenceDate, period) {
  const fyEnd = getFyEndForPeriod(referenceDate, period);
  return formatDateMdy(fyEnd);
}

export function getFiscalAnchorDate(referenceDate) {
  return formatDateMdy(getCurrentFyEnd(referenceDate));
}

function fiscalSubLabel(abbreviation, valuationDate, suffix) {
  if (!valuationDate || ['LTM', 'NTM'].includes(abbreviation)) {
    return `${abbreviation} ${suffix}`.trim();
  }
  const fyDate = formatFyEnd(valuationDate, abbreviation);
  return fyDate ? `${abbreviation}\n${fyDate}` : `${abbreviation} ${suffix}`.trim();
}

export function getFiscalColumnSubName(abbreviation, valuationDate, valueSuffix = '($)') {
  if (valueSuffix === '(%)') {
    return fiscalSubLabel(abbreviation, valuationDate, '(%)');
  }
  if (valueSuffix === '') {
    return fiscalSubLabel(abbreviation, valuationDate, '');
  }
  return fiscalSubLabel(abbreviation, valuationDate, '($)');
}
